import BaseAPI from './BaseAPI';
import Device from '../models/Device';
import {formatPhoneToPlain} from "../functions";


export class DeviceAPI extends BaseAPI {

	async updateTestMode(devices) {
		return await this.request(`/-/device/testMode`, 'PUT', {devices});
	}
	async hasSession(device) {
		return await this.request(`/-/migrate/device/${device.id}/hasSession`, 'GET');
	}
	async setDeviceMigrationStep(deviceId, step) {
		return await this.request(`/-/migrate/device/step`, 'POST', {
			deviceId, step
		});
	}
	async saveDeviceByUrl(device, action) {
		let path = window.location.pathname.replace('/d/', '');
		if(action) {
			path = `${path}/${action}`
		}
		return await this.request(`/+/device/url/${path}`, 'PUT', device.toJS());
	}
	async getDeviceByUrl() {
		const path = window.location.pathname.replace('/d/', '');
		return await this.request(`/+/device/url/${path}`, 'GET');
	}
	async createDeviceUrl(device, url) {
		return await this.request(`/-/device/url`, 'POST', {
			deviceId: device.id, url
		});
	}
	async updateDeviceUrl(device, url) {
		return await this.request(`/-/device/url`, 'PUT', {
			deviceId: device.id, url
		});
	}
	async deleteDeviceUrl(device, token) {
		return await this.request(`/-/device/url/${device.id}/${token}`, 'DELETE');
	}
	async getDeviceProtocols() {
		return await this.request(`/-/device/protocols`, 'GET');
	}
	async setFixedPosition(deviceId, latitude, longitude, altitude) {
		return await this.request(`/-/device/fixedPosition`, 'PUT', {
			deviceId, latitude, longitude, altitude
		});
	}
	async getRemoteButtons(deviceId) {
		return await this.request(`/-/device/remoteButtons/${deviceId}`, 'GET');
	}
	async getTwigCapabilities(imei) {
		return this.request(`/-/device/twig/capabilities/${imei}`);
	}
	
	async notifyNewAppVersion(device) {
		return this.request(`/-/device/notifyAppVersion/${device.id}`, 'POST');
	}
	async notifyAppPermissions(device) {
		return this.request(`/-/device/notifyAppPermissions/${device.id}`, 'POST');
	}
	
	async getSmsHistory(phone) {
		phone = formatPhoneToPlain(phone, false); 
		return this.request(`/-/device/smsHistory/${phone}`);
	}
	
	async disconnect(deviceId) {
		return this.request(`/-/device/${deviceId}/disconnect`, 'POST');
	}
	async shutDown(deviceId) {
		return this.request(`/-/device/${deviceId}/shutDown`, 'POST');
	} 
	async factoryRestore(deviceId) {
		return this.request(`/-/device/${deviceId}/factoryRestore`, 'POST');
	}
	
	async loadRecording(deviceId) {
		return this.request(`/-/device/${deviceId}/recording`);
	}

	async saveDevice(device: Device) {
		if (!device || !device.get('id')) {
			throw new Error('DeviceAPI.saveDevice: refusing to send an empty/invalid device payload');
		}
		return this.request('/-/device', 'PUT', { device: device.toJS(), forAllDevices: false });
	}

	async getStatus(device: Device) {
		return this.request(`/-/device/${device.id}/status`, 'GET');
	}

	sendMessage(message, devices, silent) {
		return this.request('/-/device/message', 'POST', {
			message,
			devices: devices.map(d => d.toJS()),
			silent
		});
	}

	async sendCommand(command: string, device: Device) {
		return this.request('/-/command', 'POST', {
			message: command,
			deviceId: device.get('id')
		});
	}
	async sendSMS(command: string, device: Device) {
		return this.request('/-/command/sms', 'POST', {
			message: command,
			deviceId: device.get('id'),
		});
	}

	async reboot(device: Device) {
		return this.request('/-/device/reboot', 'POST', {
			deviceId: device.id
		});
	}

	async requestLocation(device: Device) {
		return this.request('/-/device/locationRequest', 'POST', { deviceId: device.id });
	}

	async requestFirmware(device: Device) {
		return this.request('/-/device/firmware/' + device.id);
	}

	async sendServerConfig(device) {
		if(device.toJS) device = device.toJS();
		return this.request('/-/device/admin/environment', 'PUT', device);
	}

	async checkCode(device: Device, code: string) {
		return this.request('/-/device/activate', 'PUT', {
			deviceId: device.get('id'), code
		});
	}
	async refreshDevice(deviceId: number) {
		return this.request(`/-/device/${deviceId}`, 'GET');
	}

	async forceSettings(device: Device, onlyFailed: boolean) {
		return this.request('/-/devcon/force', 'PUT', {device, onlyFailed});
	}

	async requestAppLogs(deviceId) {
		return this.request(`/-/app/${deviceId}/logs`, 'GET');
	}
	
	async getDeviceByToken(token) {
		return await this.request(`/-/device/token/${token}`, 'GET');	
	}

}
